import { createContext, useContext, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { QRPH_TIMER } from "../../constants/qrph.c";
import { useCountdown } from "./hooks/useCountdown";
import { useQrphStatusPolling } from "./hooks/useQrphStatusPolling";
import "./style.css";

type QrphQrProps = {
  qrImage: string;
  amount?: string | number | null;
  currency?: string;
  merchantName?: string;
  address?: string;
  /** Transaction UUID to display and poll status for */
  transactionId?: string;
  onBack?: () => void;
  navigate?: (path: string) => void;
  /** Seconds allowed to scan. Defaults to 5 minutes. */
  durationSeconds?: number;
  children: ReactNode;
};

type QrphQrContextValue = Omit<QrphQrProps, "children"> & {
  durationSeconds: number;
  secondsLeft: number;
  expired: boolean;
  progress: number;
  displayAmount: string | null;
};

const QrphQrContext = createContext<QrphQrContextValue | null>(null);

const useQrphQrContext = () => {
  const context = useContext(QrphQrContext);
  if (!context) {
    throw new Error("QrphQr.* components must be rendered inside <QrphQr>");
  }
  return context;
};

const formatAmount = (amount?: string | number | null, currency?: string) => {
  if (amount === undefined || amount === null || amount === "") {
    return null;
  }
  const numeric = Number(amount);
  if (Number.isNaN(numeric)) {
    return `${currency || ""} ${amount}`.trim();
  }
  const formatted = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: Number.isInteger(numeric) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(numeric);
  return `${currency || ""} ${formatted}`.trim();
};

const formatTimer = (totalSeconds: number) => {
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

/**
 * Compound root: owns the countdown + status polling and renders the card
 * chrome. Compose the panel from the QrphQr.* subcomponents, or use the
 * default QrphQrDisplay arrangement below.
 */
const QrphQr = ({
  children,
  durationSeconds = QRPH_TIMER.SCAN_SECONDS,
  ...props
}: QrphQrProps) => {
  const { qrImage, amount, currency, transactionId, navigate } = props;
  const { secondsLeft, expired, progress } = useCountdown(
    durationSeconds,
    qrImage
  );
  useQrphStatusPolling({
    transactionId,
    navigate,
    expired,
    resetKey: qrImage,
  });

  const value: QrphQrContextValue = {
    ...props,
    durationSeconds,
    secondsLeft,
    expired,
    progress,
    displayAmount: formatAmount(amount, currency),
  };

  return (
    <QrphQrContext.Provider value={value}>
      <div className="qrph-qr-panel">
        <div className={`qrph-qr-card${expired ? " is-expired" : ""}`}>
          {children}
        </div>
      </div>
    </QrphQrContext.Provider>
  );
};

const Header = () => {
  const { t } = useTranslation();
  const { merchantName, address } = useQrphQrContext();
  return (
    <div className="qrph-qr-card-header">
      <p className="qrph-qr-eyebrow">{t("merchant.Qrph_Title")}</p>
      {merchantName ? (
        <h2 className="qrph-qr-merchant" title={merchantName}>
          {merchantName}
        </h2>
      ) : null}
      {address ? (
        <p className="qrph-qr-address" title={address}>
          {address}
        </p>
      ) : null}
    </div>
  );
};

const Amount = () => {
  const { t } = useTranslation();
  const { displayAmount } = useQrphQrContext();
  if (!displayAmount) return null;
  return (
    <div className="qrph-qr-amount-block">
      <span className="qrph-qr-amount-label">
        {t("merchant.Payment_Information.fields.Amount")}
      </span>
      <span className="qrph-qr-amount-value">{displayAmount}</span>
    </div>
  );
};

const Timer = () => {
  const { t } = useTranslation();
  const { expired, secondsLeft, durationSeconds, progress } =
    useQrphQrContext();
  return (
    <div className="qrph-qr-timer">
      <div className="qrph-qr-timer-row">
        <span className="qrph-qr-timer-label">
          {expired
            ? t("merchant.Qrph_Timer_Expired")
            : t("merchant.Qrph_Timer_Label")}
        </span>
        <span
          className={`qrph-qr-timer-value${
            secondsLeft <= QRPH_TIMER.URGENT_THRESHOLD_SECONDS
              ? " is-urgent"
              : ""
          }`}
        >
          {formatTimer(secondsLeft)}
        </span>
      </div>
      <div
        className="qrph-qr-timer-bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={durationSeconds}
        aria-valuenow={secondsLeft}
      >
        <span style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
};

const Frame = () => {
  const { t } = useTranslation();
  const { qrImage, expired } = useQrphQrContext();
  return (
    <div className={`qrph-qr-frame${expired ? " is-expired" : ""}`}>
      <img src={qrImage} alt="Qrph QR" />
      {expired ? (
        <div className="qrph-qr-expired-overlay">
          <span>{t("merchant.Qrph_Timer_Expired")}</span>
        </div>
      ) : null}
    </div>
  );
};

const Hint = () => {
  const { t } = useTranslation();
  const { expired } = useQrphQrContext();
  return (
    <p className="qrph-qr-hint">
      {expired
        ? t("merchant.Qrph_Timer_Expired_Hint")
        : t("merchant.Qrph_Scan_Hint")}
    </p>
  );
};

const Meta = () => {
  const { t } = useTranslation();
  const { transactionId } = useQrphQrContext();
  if (!transactionId) return null;
  return (
    <div className="qrph-qr-meta">
      <span className="qrph-qr-meta-label">
        {t("merchant.Qrph_Transaction_Id")}
      </span>
      <span className="qrph-qr-meta-value">{transactionId}</span>
    </div>
  );
};

const BackButton = () => {
  const { t } = useTranslation();
  const { onBack, expired } = useQrphQrContext();
  if (!onBack) return null;
  return (
    <button type="button" className="qrph-qr-back-btn" onClick={onBack}>
      {expired
        ? t("merchant.button.GenerateQrphQR")
        : t("merchant.button.cancel")}
    </button>
  );
};

QrphQr.Header = Header;
QrphQr.Amount = Amount;
QrphQr.Timer = Timer;
QrphQr.Frame = Frame;
QrphQr.Hint = Hint;
QrphQr.Meta = Meta;
QrphQr.BackButton = BackButton;

export { QrphQr };

type QrphQrDisplayProps = Omit<QrphQrProps, "children">;

/** Default arrangement of the QrphQr compound components. */
const QrphQrDisplay = (props: QrphQrDisplayProps) => (
  <QrphQr {...props}>
    <QrphQr.Header />
    <QrphQr.Amount />
    <QrphQr.Timer />
    <QrphQr.Frame />
    <QrphQr.Hint />
    <QrphQr.Meta />
    <QrphQr.BackButton />
  </QrphQr>
);

export default QrphQrDisplay;
